import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card'
import { Pill } from '@/features/feeds/components/FeedsNav'
import { AnimatedNumber, Sparkline } from '@/components/viz/Kpi'
import { BarChart, type Day } from '@/components/viz/Charts'
import { WINDOWS, type Account, type DayMetrics, type Metrics, type Sync } from '../api/growthApi'
import { useAccounts } from '../hooks/useGrowth'

// ─── Selection: account + window live in the URL, so a drill-down link keeps them ───
// The sidebar links to each Growth page without a query string, so the last choice is also kept
// in this browser: moving between pages keeps the market and window. A URL that names them wins.

const REMEMBERED = 'growth-selection'
function remembered(): { account?: string; days?: string } {
  try { return JSON.parse(localStorage.getItem(REMEMBERED) || '{}') } catch { return {} }
}

export function useSelection() {
  const [params, setParams] = useSearchParams()
  const accounts = useAccounts()
  const list = accounts.data?.data || []
  const saved = remembered()
  const accountId = params.get('account') ?? saved.account
  const account = list.find((a) => a.customerId === accountId) || list[0]
  const raw = Number(params.get('days') ?? saved.days)
  const days = (WINDOWS as readonly number[]).includes(raw) ? raw : 14
  useEffect(() => {
    try { localStorage.setItem(REMEMBERED, JSON.stringify({ account: account?.customerId, days: String(days) })) } catch { /* private mode */ }
  }, [account?.customerId, days])
  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params)
    if (!next.has('account') && account) next.set('account', account.customerId)
    if (!next.has('days')) next.set('days', String(days))
    Object.entries(patch).forEach(([k, v]) => (v == null || v === '' ? next.delete(k) : next.set(k, v)))
    setParams(next, { replace: true })
  }
  const qs = (extra: Record<string, string> = {}) => {
    const p = new URLSearchParams({ ...(account ? { account: account.customerId } : {}), days: String(days), ...extra })
    return `?${p.toString()}`
  }
  return { accounts: list, loading: accounts.isLoading, error: accounts.error, account, days, params, set, qs }
}

// ─── Formatting ─────────────────────────────────────────

const SYMBOL: Record<string, string> = { USD: '$', GBP: '£', EUR: '€' }

export const money = (v: number | null | undefined, currency = 'USD', digits = 2) =>
  v == null ? '—' : `${SYMBOL[currency] || ''}${v.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}${SYMBOL[currency] ? '' : ` ${currency}`}`
export const num = (v: number | null | undefined) => (v == null ? '—' : v.toLocaleString(undefined, { maximumFractionDigits: 2 }))
export const pct = (v: number | null | undefined, digits = 1) => (v == null ? '—' : `${(v * 100).toFixed(digits)}%`)
export const ratio = (v: number | null | undefined) => (v == null ? '—' : `${v.toFixed(2)}×`)
export const shortDate = (d: string) => new Date(d + 'T00:00:00Z').toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })

// ─── Frame ──────────────────────────────────────────────
// Growth's pages (Command center, Search terms, Products, Strategy) are sidebar sub-items, so the
// frame is the title, the market and window pickers, and the sync state.

function SyncNote({ sync }: { sync?: Sync | null }) {
  if (!sync) return <span>never synced</span>
  const at = sync.finished_at ? formatDistanceToNow(new Date(sync.finished_at), { addSuffix: true }) : 'running'
  return (
    <span title={sync.error || undefined}>
      Nightly sync {sync.status === 'ok' ? '' : `${sync.status} `}{at}
    </span>
  )
}

/** Page frame for Growth: title, account + window pickers, sync state. */
export function GrowthFrame({ title, description, children, hideWindow }: {
  title: string
  description: React.ReactNode
  children: React.ReactNode
  hideWindow?: boolean
}) {
  const sel = useSelection()
  return (
    <div className="space-y-6 p-6 md:p-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="font-serif text-3xl font-semibold tracking-tight">{title}</h1>
          <p className="mt-1 max-w-4xl text-sm text-muted-foreground">{description}</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex flex-wrap items-center justify-end gap-2">
            {sel.accounts.length > 1 && (
              <Segmented
                label="Market"
                value={sel.account?.customerId}
                options={sel.accounts.map((a) => ({ value: a.customerId, label: `${a.market} · ${a.currency}` }))}
                onChange={(v) => sel.set({ account: v })}
              />
            )}
            {!hideWindow && (
              <Segmented
                label="Window"
                value={String(sel.days)}
                options={WINDOWS.map((d) => ({ value: String(d), label: `${d}d` }))}
                onChange={(v) => sel.set({ days: v })}
              />
            )}
          </div>
          {sel.account && (
            <span className="text-xs text-muted-foreground">
              {sel.account.name} · <SyncNote sync={sel.account.sync} />
            </span>
          )}
        </div>
      </div>
      {sel.error ? <ErrorNote error={sel.error} /> : children}
    </div>
  )
}

export function Segmented({ label, value, options, onChange }: {
  label: string
  value?: string
  options: Array<{ value: string; label: string }>
  onChange: (v: string) => void
}) {
  return (
    <div role="group" aria-label={label} className="inline-flex rounded-md border bg-card p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          aria-pressed={value === o.value}
          className={`rounded px-2.5 py-1 text-xs font-medium ${value === o.value ? 'bg-foreground text-background' : 'text-muted-foreground hover:bg-muted'}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

export function ErrorNote({ error }: { error: unknown }) {
  const e = error as { response?: { data?: { message?: string; error?: string } }; message?: string }
  return (
    <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
      {e.response?.data?.message || e.response?.data?.error || e.message || 'Could not load'}
    </div>
  )
}

// ─── KPI tile ───────────────────────────────────────────

/**
 * A headline number with its change against the previous window. `good` says which direction is
 * better. Pass `amount` + `format` to count up to the value, and `spark` for its daily trend.
 */
export function Kpi({ label, value, cur, prev, good = 'up', hint, tone, amount, format, spark, delay = 0 }: {
  label: string
  value: string
  cur?: number | null
  prev?: number | null
  good?: 'up' | 'down' | 'none'
  hint?: React.ReactNode
  tone?: 'warn' | 'ok'
  amount?: number | null
  format?: (v: number) => string
  spark?: { values: number[]; color: string }
  delay?: number
}) {
  let delta: React.ReactNode = null
  if (cur != null && prev != null && good !== 'none') {
    if (!prev) delta = cur ? <span className="text-muted-foreground">new</span> : null
    else {
      const ch = (cur - prev) / Math.abs(prev)
      const better = good === 'up' ? ch > 0 : ch < 0
      const flat = Math.abs(ch) < 0.005
      // Arrow and words carry the direction; color only reinforces it.
      delta = (
        <span className={flat ? 'text-muted-foreground' : better ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}
          title={flat ? 'unchanged' : better ? 'better than the previous window' : 'worse than the previous window'}>
          {flat ? '→' : ch > 0 ? '▲' : '▼'} {Math.abs(ch * 100).toFixed(0)}%
        </span>
      )
    }
  }
  const ring = tone === 'warn' ? 'border-amber-400/70' : tone === 'ok' ? 'border-emerald-400/70' : ''
  return (
    <Card className={`viz-rise transition-shadow hover:shadow-card ${ring}`} style={{ animationDelay: `${delay}ms` }}>
      <CardHeader className="pb-1">
        <CardDescription className="flex items-center justify-between gap-2 text-xs uppercase tracking-wide">
          {label}
          {tone && <span className="normal-case tracking-normal">{tone === 'ok' ? 'above break-even' : 'below break-even'}</span>}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-0.5">
        <div className="font-serif text-3xl font-semibold tabular-nums">
          {amount != null && format ? <AnimatedNumber value={amount} format={format} /> : value}
        </div>
        <div className="flex flex-wrap gap-x-2 text-xs text-muted-foreground">
          {delta}
          {hint}
        </div>
        {spark && spark.values.length > 1 && <div className="pt-2"><Sparkline values={spark.values} color={spark.color} label={`${label} per day`} /></div>}
      </CardContent>
    </Card>
  )
}

// ─── Day bars: one measure per chart (never two y-scales), hover or arrow keys for the day ───

export function DayBars<T extends { date: string }>({ title, rows, value, format, height = 160, color = 'var(--viz-sessions)' }: {
  title: string
  rows: T[]
  value: (r: T) => number
  format: (v: number) => string
  height?: number
  color?: string
}) {
  const total = rows.reduce((s, r) => s + value(r), 0)
  const data = rows.map((r) => ({ day: r.date, v: value(r) })) as unknown as Day[]
  return (
    <Card className="viz-rise">
      <CardHeader className="pb-2">
        <div className="flex items-baseline justify-between gap-2">
          <CardDescription className="text-xs uppercase tracking-wide">{title}</CardDescription>
          <span className="text-xs tabular-nums text-muted-foreground">Total <span className="font-medium text-foreground">{format(total)}</span></span>
        </div>
      </CardHeader>
      <CardContent>
        <BarChart data={data} series={[{ key: 'v', label: title, color }]} format={format} height={height} label={`${title} per day`} />
      </CardContent>
    </Card>
  )
}

/** A thin share meter: impression share won, lost to rank, lost to budget. */
export function ShareBar({ won, rank, budget }: { won: number | null; rank: number | null; budget: number | null }) {
  if (won == null) return <span className="text-xs text-muted-foreground">—</span>
  const segs = [
    { v: won, c: 'hsl(var(--foreground) / 0.65)', l: 'won' },
    { v: rank || 0, c: 'var(--viz-warning)', l: 'lost to rank (bid/quality)' },
    { v: budget || 0, c: 'var(--viz-critical)', l: 'lost to budget' },
  ]
  return (
    <div className="w-36" title={segs.map((s) => `${s.l} ${pct(s.v, 0)}`).join(' · ')}>
      <div className="flex h-2 gap-[2px] overflow-hidden rounded-full bg-muted">
        {segs.map((s) => (s.v > 0 ? <div key={s.l} style={{ width: `${s.v * 100}%`, background: s.c }} /> : null))}
      </div>
      <div className="mt-0.5 text-[10px] text-muted-foreground">
        {pct(won, 0)} won · {pct(rank, 0)} rank · {pct(budget, 0)} budget
      </div>
    </div>
  )
}

export function StatusPill({ status }: { status: string }) {
  if (status === 'ENABLED') return <Pill tone="green">enabled</Pill>
  if (status === 'PAUSED') return <Pill tone="amber">paused</Pill>
  return <Pill tone="slate">{status.toLowerCase()}</Pill>
}

export const metricCells = (m: Metrics, currency: string) => ({
  cost: money(m.cost, currency),
  clicks: num(m.clicks),
  impressions: num(m.impressions),
  ctr: pct(m.ctr),
  cpc: m.clicks ? money(m.cpc, currency) : '—',
  purchases: num(m.purchases),
  value: money(m.value, currency),
  roas: m.cost ? ratio(m.roas) : '—',
})

export type { Account, DayMetrics }
